import { Button } from "@modulz/design-system";
import { useCallback, useEffect, useState } from "preact/hooks";
import Select, { StylesConfig } from "react-select";

import { VerticalSpace } from "../components/vertical-space";
import { DatasetMap } from "../graphpad.types";
import { postMessageToFigjam } from "./post-message-to-figjam";
import { SPEC_PRESETS } from "./spec-presets";
import { VegaEditor } from "./vega-editor";

// import * as Select from "@radix-ui/react-select";
// import Select from './custom-select';
import { h, Fragment, ComponentType } from "preact";

// import { Editor } from "../editor";
// import { highlight, languages } from "prismjs/components/prism-core";
// import "prismjs/components/prism-json";
// Mark as global to work w/esbuild
// import "prismjs/themes/prism.css";
// import { Tooltip } from "@modulz/design-system";

const THEMES = new Set([
  "no theme",
  "dark",
  "excel",
  "googlecharts",
  "ggplot2",
  "fivethirtyeight",
  "latimes",
  "quartz",
  "vox",
  "urbaninstitute",
]);

export interface GroupedOption {
  readonly label: string;
  readonly options: readonly SelectOption[];
}

interface SelectOption {
  value: string;
  label: string;
}

const addLabel = (group: {
  readonly label: string;
  readonly options: readonly { value: string }[];
}): GroupedOption => ({
  label: group.label,
  options: group.options.map((option) => ({
    value: option.value,
    label: option.value,
  })),
});

const presetOptions: GroupedOption[] = [
  {
    label: "Sample Graphs",
    options: [
      { value: "horizontalBar" },
      { value: "scatterplot" },
      { value: "heatmap" },
      { value: "pie" },
      { value: "lineChartCustomWidth" },
    ],
  },
  {
    label: "Annotation Examples",
    options: [
      { value: "rainbowBar" },
      { value: "barFromDatasetsTab" },
      { value: "barWithAnnotation" },
      { value: "filteredScatterplot" },
      { value: "linearRegressionScatterplot" },
    ],
  },
  {
    label: "Advanced views",
    options: [
      { value: "animalIsotype" },
      { value: "emojiIsotype" },
      { value: "sineWave" },
      { value: "orthographicGraticule" },
    ],
  },
].map((g) => addLabel(g));

// from https://stackoverflow.com/questions/54218351/changing-height-of-react-select-component
const customSelectStyles: StylesConfig<SelectOption> = {
  control: (provided, state) => ({
    ...provided,
    background: "#fff",
    borderColor: "#9e9e9e",
    minHeight: "30px",
    height: "30px",
    boxShadow: state.isFocused ? undefined : undefined,
  }),

  valueContainer: (provided) => ({
    ...provided,
    height: "30px",
    padding: "0 6px",
  }),

  input: (provided) => ({
    ...provided,
    margin: "0px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "30px",
  }),
  container: (provided) => ({
    ...provided,
    width: 220,
    display: "inline-block",
  }),
};

export const PresetDropdown: ComponentType<{
  setSpec: (text: string) => void;
}> = (props) => {
  const [value, setValue] = useState<SelectOption>();

  function handleChange(val: SelectOption | null) {
    if (val) {
      const newValue = val.value;
      setValue(val);
      // console.log(newValue);
      if (newValue in SPEC_PRESETS) {
        const preset = SPEC_PRESETS[newValue as any];
        props.setSpec(JSON.stringify(preset, null, 2));
      }
    }
  }

  return (
    // <Dropdown onChange={handleChange as any} options={options} value={value} />
    <Select
      onChange={handleChange}
      placeholder="Load preset"
      options={presetOptions as any}
      value={value}
      styles={customSelectStyles}
    />
  );
};

const groups: GroupedOption[] = [
  {
    label: "-- Optional --",
    options: [{ value: "no theme" }],
  },
  {
    label: "Tool-based",
    options: [
      { value: "dark" },
      { value: "excel" },
      { value: "googlecharts" },
      { value: "ggplot2" },
    ],
  },
  {
    label: "Journalism-based",
    options: [
      { value: "fivethirtyeight" },
      { value: "latimes" },
      { value: "quartz" },
      { value: "vox" },
      { value: "urbaninstitute" },
    ],
  },
].map((g) => addLabel(g));
export const ThemeDropdown: ComponentType<{
  setTheme: (text: string) => void;
  initialTheme: string;
}> = (props) => {
  const { initialTheme } = props;
  const [value, setValue] = useState<SelectOption | null>(
    THEMES.has(initialTheme)
      ? { value: initialTheme, label: initialTheme }
      : { value: "no theme", label: "no theme" }
  );

  function handleChange(value: SelectOption | null) {
    if (value === null) {
      setValue(null);
    } else {
      setValue(value);
      if (THEMES.has(value.value as string)) {
        props.setTheme(value.value);
      }
    }
  }

  return (
    <Select
      onChange={handleChange}
      placeholder="Optional: Choose a theme"
      options={groups as any}
      value={value}
      styles={customSelectStyles}
    />
  );
};

export const TableSelectorDropdown: ComponentType<{
  datasets: DatasetMap;
  spec: string;
  setSpec: (text: string) => void;
}> = (props) => {
  const { datasets, spec } = props;
  const [value, setValue] = useState<SelectOption | null>();
  const options = [
    // { value: "-- Optional: Choose data" },
    ...Object.keys(datasets).map((d) => ({ value: d, label: d })),
  ];
  function handleChange(item: SelectOption | null) {
    if (item) {
      const newValue = item.value;
      setValue(item);
      // console.log({ newValue });
      if (datasets[newValue]) {
        // console.log("replacing");
        const parsedSpec = JSON.parse(spec);
        parsedSpec["data"] = { name: newValue };
        props.setSpec(JSON.stringify(parsedSpec, null, 2));
      }
    }
  }

  return (
    <Select
      onChange={handleChange}
      placeholder="Optional: Choose a dataset"
      options={options}
      value={value}
      styles={customSelectStyles}
    />
  );
  // return <Dropdown onChange={handleChange} options={options} value={value} />;
};

export const SpecPanel: ComponentType<{
  datasets: DatasetMap;
  initialTheme: string;
  text: string;
  onSpecChange: (spec: string) => void;
  onThemeChange: (theme: string) => void;
}> = (props) => {
  const { text, onThemeChange, onSpecChange } = props;

  const [graphDefinition, setGraphDefinition] = useState<string>(text);

  // Sync dropdown updates with the table spe
  useEffect(() => {
    setGraphDefinition(text);
  }, [text]);

  // We're OK sending this instead of raw SVG
  // since we're not going to permit doing external data fetching in the MVP.
  // permitting that right out the gate could be a security risk.
  const handleUpdateTheme = useCallback(
    (newTheme: string) => {
      onThemeChange(newTheme);
    },
    [onThemeChange]
  );

  return (
    <Fragment>
      <VerticalSpace space="small" />

      <div>
        <Button
          variant={"green"}
          onClick={() => {
            onSpecChange(graphDefinition);
          }}
        >
          Run graph
        </Button>
        <div style={{ width: 8, display: "inline-block" }} />

        <Button
          onClick={() => {
            try {
              const parsed = JSON.parse(graphDefinition);
              setGraphDefinition(JSON.stringify(parsed, null, 2));
            } catch (error) {
              postMessageToFigjam({
                type: "NOTIFICATION",
                data: {
                  message: "Error parsing graph definition",
                },
              });
              console.error(error);
            }
          }}
        >
          Format (JSON)
        </Button>
        {/* <div style={{ width: 8, display: "inline-block" }}></div>

            <Button>
              <a href="https://vega.github.io/vega-lite/examples/">Help</a>
            </Button> */}
      </div>

      <VerticalSpace space="small" />

      <div
        style={{
          display: "grid",
          gridGap: "8px",
          gridAutoFlow: "column",
          justifyContent: "start",
        }}
      >
        <PresetDropdown
          setSpec={(newText: string) => {
            onSpecChange(newText);
          }}
        />

        <ThemeDropdown
          initialTheme={props.initialTheme}
          setTheme={(newTheme: string) => {
            handleUpdateTheme(newTheme);
          }}
        />
      </div>

      <VerticalSpace space="small" />
      {Object.keys(props.datasets).length > 0 && (
        <div>
          <TableSelectorDropdown
            datasets={props.datasets}
            spec={graphDefinition}
            setSpec={(newText: string) => {
              onSpecChange(newText);
            }}
          />
        </div>
      )}

      <VerticalSpace space="small" />

      <VegaEditor
        spec={graphDefinition}
        setSpec={(code) => {
          setGraphDefinition(code);
        }}
        onSubmit={(spec) => {
          setGraphDefinition(spec);
          props.onSpecChange(spec);
        }}
      />
      <div style={{ width: 8, display: "inline-block" }} />
    </Fragment>
  );
};
